import React, { useEffect, useState, useRef } from 'react';
import { CameraIcon, LoaderIcon, TrashIcon } from 'lucide-react';
import { db, storage } from '../../firebase';
import { doc, updateDoc, collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { compressImage } from '../../utils/imageUtils';
import toast from 'react-hot-toast';
interface StudentProfilePhotoUploadProps {
  uid: string;
  currentPhotoUrl: string | null;
  firstName: string;
  lastName: string;
  isDark: boolean;
  onPhotoUpdated: (newUrl: string | null) => void;
}
export const StudentProfilePhotoUpload: React.FC<StudentProfilePhotoUploadProps> = ({
  uid,
  currentPhotoUrl,
  firstName,
  lastName,
  isDark,
  onPhotoUpdated
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMounted = useRef(true);
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  const getInitials = () => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${first}${last}` || '?';
  };
  /**
   * Sync profile image URL to all teacher student subcollection docs
   * where this student is linked. Non-blocking — failures are logged but don't affect UX.
   */
  const syncToTeacherStudentDocs = async (imageUrl: string | null) => {
    try {
      const studentsQuery = query(collectionGroup(db, 'students'), where('linkedUid', '==', uid));
      const snapshot = await getDocs(studentsQuery);
      const updatePromises = snapshot.docs.map(docSnap => updateDoc(docSnap.ref, {
        profileImageUrl: imageUrl
      }).catch(err => {
        console.warn('[ProfilePhoto] Failed to sync to teacher doc:', docSnap.ref.path, err);
      }));
      await Promise.all(updatePromises);
      console.log(`[ProfilePhoto] Synced profileImageUrl to ${snapshot.size} teacher student doc(s)`);
    } catch (error) {
      console.warn('[ProfilePhoto] Non-blocking sync to teacher docs failed:', error);
    }
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a JPG, PNG, or WebP image');
      return;
    }
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    setIsUploading(true);
    setUploadProgress(0);
    try {
      // 1. Compress image
      let fileToUpload: File | Blob = file;
      try {
        const compressedBlob = await compressImage(file);
        fileToUpload = new File([compressedBlob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
      } catch (compressError) {
        console.warn('Image compression failed, using original file', compressError);
      }
      if (!isMounted.current) return;
      // 2. Upload to Firebase Storage
      const timestamp = new Date().getTime();
      const filename = `${timestamp}_${file.name.replace(/\s+/g, '_')}`;
      const storagePath = `profile-photos/${uid}/${filename}`;
      const storageRef = ref(storage, storagePath);
      const metadata = {
        contentType: fileToUpload.type,
        customMetadata: {
          uploadedBy: uid,
          originalFilename: file.name,
          userType: 'student'
        }
      };
      const uploadTask = uploadBytesResumable(storageRef, fileToUpload, metadata);
      await new Promise<void>((resolve, reject) => {
        uploadTask.on('state_changed', snapshot => {
          if (!isMounted.current) return;
          const progress = snapshot.bytesTransferred / snapshot.totalBytes * 100;
          setUploadProgress(Math.round(progress));
        }, error => {
          reject(error);
        }, () => {
          resolve();
        });
      });
      if (!isMounted.current) return;
      // 3. Get download URL
      const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
      if (!isMounted.current) return;
      // 4. Update Firestore
      const userDocRef = doc(db, 'users', uid);
      await updateDoc(userDocRef, {
        profileImageUrl: downloadURL,
        profileImageUpdatedAt: new Date(),
        updatedAt: new Date()
      });
      if (!isMounted.current) return;
      // 5. Sync to teacher student subcollection docs (non-blocking)
      syncToTeacherStudentDocs(downloadURL);
      // 6. Success
      onPhotoUpdated(downloadURL);
      toast.success('Profile photo updated successfully');
    } catch (error: any) {
      console.error('Error uploading profile photo:', error);
      if (error.code === 'storage/unauthorized') {
        toast.error('Upload failed: Permission denied');
      } else {
        toast.error('Failed to upload profile photo. Please try again.');
      }
    } finally {
      if (isMounted.current) {
        setIsUploading(false);
        setUploadProgress(0);
      }
    }
  };
  const handleDeletePhoto = async () => {
    if (!currentPhotoUrl || isDeleting) return;
    setIsDeleting(true);
    try {
      const userDocRef = doc(db, 'users', uid);
      await updateDoc(userDocRef, {
        profileImageUrl: null,
        profileImageUpdatedAt: new Date(),
        updatedAt: new Date()
      });
      if (!isMounted.current) return;
      // Sync removal to teacher student subcollection docs (non-blocking)
      syncToTeacherStudentDocs(null);
      onPhotoUpdated(null);
      toast.success('Profile photo removed');
    } catch (error) {
      console.error('Error deleting profile photo:', error);
      toast.error('Failed to remove profile photo');
    } finally {
      if (isMounted.current) {
        setIsDeleting(false);
      }
    }
  };
  return <div className="flex flex-col items-center sm:flex-row sm:items-start gap-6" data-id="element-11574">
      {/* Avatar Container */}
      <div className="relative group" data-id="element-11575">
        <div className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden border-4 flex items-center justify-center transition-colors ${isDark ? 'border-navy-800 bg-navy-700' : 'border-white bg-gray-100 shadow-sm'}`} data-id="element-11576">
          {isUploading ? <div className="flex flex-col items-center justify-center w-full h-full bg-black/40 backdrop-blur-sm" data-id="element-11577">
              <LoaderIcon className="w-6 h-6 text-white animate-spin mb-1" data-id="element-11578" />
              <span className="text-white text-xs font-medium" data-id="element-11579">
                {uploadProgress}%
              </span>
            </div> : currentPhotoUrl ? <img src={currentPhotoUrl} alt={`${firstName} ${lastName}`} className="w-full h-full object-cover" onError={e => {
          // Fallback if image fails to load
          e.currentTarget.style.display = 'none';
        }} data-id="element-11580" /> : <span className={`text-2xl sm:text-3xl font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11581">
              {getInitials()}
            </span>}
        </div>

        {/* Hover Overlay (only show if not uploading) */}
        {!isUploading && <button onClick={() => fileInputRef.current?.click()} className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer" aria-label="Change profile photo" data-id="element-11582">
            <CameraIcon className="w-8 h-8 text-white" data-id="element-11583" />
          </button>}

        {/* Hidden File Input */}
        <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/jpeg, image/png, image/webp" className="hidden" data-id="element-11584" />
      </div>

      {/* Actions */}
      <div className="flex flex-col items-center sm:items-start justify-center pt-2 sm:pt-4" data-id="element-11585">
        <h3 className={`text-lg font-medium mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-11586">
          Profile Photo
        </h3>
        <p className={`text-sm mb-4 text-center sm:text-left ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-11587">
          JPG, PNG or WebP. Max size of 5MB.
        </p>

        <div className="flex items-center gap-3" data-id="element-11588">
          <button onClick={() => fileInputRef.current?.click()} disabled={isUploading || isDeleting} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 shadow-sm'}`} data-id="element-11589">
            {currentPhotoUrl ? 'Change Photo' : 'Upload Photo'}
          </button>

          {currentPhotoUrl && <button onClick={handleDeletePhoto} disabled={isUploading || isDeleting} className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${isDark ? 'text-red-400 hover:bg-red-400/10' : 'text-red-600 hover:bg-red-50'}`} aria-label="Remove photo" data-id="element-11590">
              {isDeleting ? <LoaderIcon className="w-5 h-5 animate-spin" data-id="element-11591" /> : <TrashIcon className="w-5 h-5" data-id="element-11592" />}
            </button>}
        </div>
      </div>
    </div>;
};